import {Component, OnInit, signal} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {NbButtonModule, NbCheckboxModule, NbInputModule, NbRadioModule, NbSpinnerModule, NbToastrService} from '@nebular/theme';
import {notificationsParams} from '../../../shared/settings';
import {TeamsService} from '../../../services/teams.service';
import {LeagueComponent} from '../../../shared/components/league/league.component';
import {LeagueCenteredComponent} from '../../../shared/components/league-centered/league-centered.component';
import {ContactComponent} from '../../../shared/components/contact/contact.component';
import {Title} from '@angular/platform-browser';
import {DecimalPipe} from "@angular/common";
import {TranslateModule} from '@ngx-translate/core';

@Component({
  selector: 'app-sign-up-v2',
  standalone: true,
  templateUrl: './sign-up-v2.component.html',
  styleUrl: './sign-up-v2.component.scss',
  imports: [
    TranslateModule,ReactiveFormsModule, NbButtonModule, NbCheckboxModule, NbRadioModule, NbSpinnerModule, LeagueComponent, LeagueCenteredComponent, ContactComponent, NbInputModule, DecimalPipe]
})
export class SignUpV2Component implements OnInit {
  activisionMinLength = 2;
  activisionMaxLength = 50;

  preferredDivisions: any[] = [];
  nextSeason: any = null;
  registrationOpen = false;
  registrationChecked = false;
  loading = signal<boolean>(false);

  currentStep = 1;

  submitted = false;
  submittedData: any = null;

  registerForm!: FormGroup;
  currentTitle!: string;
  lowTierInfoDocUrl: any;

constructor(
    private readonly title: Title,
    private teamsService: TeamsService,
    private toastrService: NbToastrService
  ) {}

  ngOnInit(): void {
    this.currentTitle = this.title.getTitle();

    this.registerForm = new FormGroup({
      preferred_division: new FormControl('', [Validators.required]),
      team_name: new FormControl('', [Validators.required]),
      team_account_x: new FormControl(''),
      team_manager_name: new FormControl('', [Validators.required]),
      captain_email: new FormControl('', [Validators.required, Validators.email]),
      captain_account_x: new FormControl(''),
      captain_account_discord: new FormControl(''),
      captain_account_activision: new FormControl('', [
        Validators.required,
        Validators.minLength(this.activisionMinLength),
        Validators.maxLength(this.activisionMaxLength)
      ]),
      player_2_account_activision: new FormControl(''),
      player_3_account_activision: new FormControl(''),
      player_4_account_activision: new FormControl(''),
      player_5_account_activision: new FormControl(''),
      player_6_account_activision: new FormControl(''),
      cmg_account: new FormControl(''),
      i_agree: new FormControl(false, [this.conditionalRequiredValidator(this.currentStep == 2)]),
      i_understand: new FormControl(false, [this.conditionalRequiredValidator(this.currentStep == 2)]),
    });

    this.loading.set(true);
    this.teamsService.registrationStatus().subscribe({
      next: (response: any) => {
        this.lowTierInfoDocUrl = response.low_tier_info_doc_url;
        this.registrationOpen = response.open;
        this.nextSeason = response.next_season;
        this.registrationChecked = true;

        if (this.registrationOpen) {
          this.teamsService.divisions().subscribe({
            next: (divisions: any) => {
              for (const division of divisions) {
                this.preferredDivisions.push(division);
              }
              // this.preferredDivisions.push({division_id: 'other', name: 'Other'});
            },
            complete: () => { this.loading.set(false); }
          });
        } else {
          this.loading.set(false);
        }
      },
      error: () => {
        this.registrationOpen = false;
        this.registrationChecked = true;
        this.loading.set(false);
      }
    });
  }

  preferredDivisionLabel(): string {
    if (!this.registerForm.value.preferred_division) return '—';
    const found = this.preferredDivisions.find(d => d.division_id === this.registerForm.value.preferred_division);
    return found ? found.name : this.registerForm.value.preferred_division;
  }

  activisionPlayers(): string[] {
    const v = this.registerForm.value;
    return [
      v.captain_account_activision,
      v.player_2_account_activision,
      v.player_3_account_activision,
      v.player_4_account_activision,
      v.player_5_account_activision,
      v.player_6_account_activision,
    ].filter(p => !!p);
  }

  conditionalRequiredValidator(condition: boolean) {
    return (control: AbstractControl) => {
      if (condition && !control.value) {
        return { required: true };
      }
      return null;
    };
  }

  next() {
    this.currentStep = 2;
  }

  back() {
    this.currentStep = 1;
    this.registerForm.controls['i_agree'].markAsUntouched();
    this.registerForm.controls['i_agree'].markAsPristine();
    this.registerForm.controls['i_agree'].setErrors(null);
    this.registerForm.controls['i_understand'].markAsUntouched();
    this.registerForm.controls['i_understand'].markAsPristine();
    this.registerForm.controls['i_understand'].setErrors(null);
  }

  onSubmit() {
    if (this.registerForm.invalid) return;

    this.loading.set(true);
    this.teamsService.registerV2(this.registerForm.value).subscribe({
      next: () => {
        this.submittedData = this.registerForm.value;
        this.submitted = true;
        this.loading.set(false);
      },
      error: (e) => {
        const message = e?.error?.message || e?.message;
        this.toastrService.danger(message, 'Error', notificationsParams);
        this.loading.set(false);
      }
    });
  }
}
